import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FolderPen, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const SheetContextEditor = ({ sheet, canEdit, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ customer_id: sheet.customer_id || "", project_id: sheet.project_id || "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ customer_id: sheet.customer_id || "", project_id: sheet.project_id || "" });
    Promise.all([axios.get(`${API}/customers`), axios.get(`${API}/projects`)]).then(([customerResponse, projectResponse]) => {
      setCustomers(customerResponse.data || []);
      setProjects(projectResponse.data || []);
    }).catch(() => toast.error("Could not load clients and projects"));
  }, [open, sheet.customer_id, sheet.project_id]);

  const availableProjects = useMemo(() => projects.filter(project => !form.customer_id || project.lead_id === form.customer_id), [projects, form.customer_id]);
  const save = async () => {
    if (!form.customer_id && !form.project_id) return toast.error("Select a client or project");
    setSaving(true);
    try {
      const response = await axios.put(`${API}/production-sheets/${sheet.sheet_id}`, { customer_id: form.customer_id || null, project_id: form.project_id || null });
      onSaved(response.data);
      setOpen(false);
      toast.success("Client and project updated");
    } catch (error) { toast.error(error.response?.data?.detail || "Could not update client and project"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button type="button" onClick={() => canEdit && setOpen(true)} disabled={!canEdit} className="mt-1 flex items-center gap-1 text-left text-sm text-slate-500 hover:text-slate-800 disabled:cursor-default" data-testid="sheet-context-edit-button">
        <FolderPen className="h-3.5 w-3.5" /> {sheet.customer_name || "Choose client"}{sheet.project_name ? ` · ${sheet.project_name}` : " · No project"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="sheet-context-dialog">
          <DialogHeader><DialogTitle>Edit client and project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Client</Label><select className="mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm" value={form.customer_id} onChange={event => setForm({ customer_id: event.target.value, project_id: "" })} data-testid="sheet-context-customer-select"><option value="">— select client —</option>{customers.map(customer => <option key={customer.lead_id} value={customer.lead_id}>{customer.first_name} {customer.last_name}</option>)}</select></div>
            <div><Label>Project (optional)</Label><select className="mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm" value={form.project_id} onChange={event => { const project = projects.find(item => item.project_id === event.target.value); setForm({ project_id: event.target.value, customer_id: project?.lead_id || form.customer_id }); }} data-testid="sheet-context-project-select"><option value="">No project — client history only</option>{availableProjects.map(project => <option key={project.project_id} value={project.project_id}>{project.name}</option>)}</select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} data-testid="sheet-context-cancel-button">Cancel</Button><Button onClick={save} disabled={saving || (!form.customer_id && !form.project_id)} data-testid="sheet-context-save-button">{saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};